// PHONE WEB: who is on a phone browser, and what have they already decided about the app.
//
// This is the client half of a question the site answers in two places. The server half is
// functions/_device.js, which reads the same signals off the request headers so a Pages
// Function can render a different page per device with no flash. Keep the two honest with
// each other; they share the crawler list and the ?desktop=1 escape hatch by construction.
//
// Why this file exists at all, when appPromo.ts already had isPhoneBrowser(): appPromo answers
// "should the WALL replace the reader", a single boolean that has been false since 2026-08-25.
// The product question now is different and has three states, not two: a phone visitor is
// either unasked, has taken the app, or has declined it, and the reader they get differs in
// each case. Storing that decision, and reading it before first paint, is this module's job.

import { isCrawlerBrowser, wallBypassed, webPlatform, type WebPlatform } from "./appPromo";
import { isNativeApp } from "./native";
import { isStandalone } from "./pwaInstall";

export type { WebPlatform };

/**
 * A phone, by the best signal available rather than by user agent alone.
 *
 * `navigator.userAgentData.mobile` is a real browser-reported boolean, not a string we are
 * pattern matching, and Chromium ships it, which covers most of the Android traffic this
 * product actually receives. Where it is absent (Safari, Firefox, every in-app webview that
 * has not adopted client hints) we fall back to the user agent, which is what appPromo has
 * always done.
 *
 * Deliberately NOT a viewport-width test. A narrow desktop window is not a phone, and the
 * thing being decided here is "would the native app be better for you", which is a question
 * about the device, not about how wide the window happens to be right now.
 */
export function isPhoneDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData && typeof uaData.mobile === "boolean") {
    // A tablet reports mobile:false, which is the answer we want: tablets keep the full reader.
    return uaData.mobile;
  }
  const ua = navigator.userAgent || "";
  if (!ua) return false;
  if (/iphone|ipod/i.test(ua)) return true;
  // Android phones carry "Mobile" in the UA; Android tablets do not.
  return /android/i.test(ua) && /mobile/i.test(ua);
}

/**
 * Is this THE surface the app pitch is for: a phone, in a browser, on the open web?
 *
 * Every clause is a way of answering no, because each false positive pitches an app to
 * someone who cannot use the pitch:
 *  - the native app is this same bundle, so pitching there is pitching to itself,
 *  - an installed PWA already took an install; asking again is an insult,
 *  - crawlers and unfurlers must see the reader, or indexing and link previews break,
 *  - ?desktop=1 lets a real phone inspect the untouched reader without editing code.
 */
export function isPhoneWeb(): boolean {
  if (isNativeApp) return false;
  if (isStandalone()) return false;
  if (wallBypassed()) return false;
  if (isCrawlerBrowser()) return false;
  return isPhoneDevice();
}

// ── What the visitor has decided ────────────────────────────────────────────

/** `unasked` has never seen the door. `declined` chose the browser. `taken` tapped through to
 *  a store, which we cannot verify installed, so it only ever means "do not ask again". */
export type AppChoice = "unasked" | "declined" | "taken";

const CHOICE_KEY = "insnip.appChoice";

export function appChoice(): AppChoice {
  try {
    const v = localStorage.getItem(CHOICE_KEY);
    return v === "declined" || v === "taken" ? v : "unasked";
  } catch {
    // No storage (private mode, or a webview with cookies blocked). "unasked" would show the
    // door on every single page load, which is the one behaviour guaranteed to lose the
    // reader, so an unreadable store resolves to "leave them alone".
    return "declined";
  }
}

export function setAppChoice(choice: AppChoice): void {
  try {
    localStorage.setItem(CHOICE_KEY, choice);
  } catch {
    /* storage unavailable: the choice holds for this page view and that is all we can offer */
  }
}

/** The full-screen door is shown once, to a phone visitor who has never answered it. */
export function shouldShowPhoneDoor(): boolean {
  return isPhoneWeb() && appChoice() === "unasked";
}

/**
 * Did this visitor arrive ON a particular story, rather than at the round in general?
 *
 * A shared link, a push notification and a referral all name a story, and someone who tapped
 * a headline came for that headline. The door still happens to them, it just happens one
 * story later: they read the thing they came for first, and the app is offered to someone who
 * has now seen the product work rather than to a stranger holding a sales pitch where their
 * article should be.
 *
 * That order is not a matter of taste here, it is the mistake this codebase has already made
 * and measured. functions/read.js and appPromo.ts both carry the note: the phone wall put an
 * install between a first-time visitor and their first story, and 78% of readers read exactly
 * one card. An app install cannot rescue a session that never happened.
 *
 * The tests for this shape live beside shouldEnterReader in functions/_landing.test.ts; the
 * `r=` boundary match is deliberate for the same reason it is there, so ?partner= and ?year=
 * do not read as referrals.
 */
export function arrivedOnAStory(search?: string, hash?: string): boolean {
  const qs = search ?? (typeof location === "undefined" ? "" : location.search || "");
  const h = hash ?? (typeof location === "undefined" ? "" : location.hash || "");
  if (h.indexOf("card=") !== -1) return true;
  if (/(?:^|[?&])(?:ref|r)=/.test(qs)) return true;
  return /(?:^|[?&])from=share/.test(qs);
}

/**
 * The answers after which the standing strip still stands. USER, 2026-09-08: ALL of them.
 *
 * It used to be `declined` alone, on the reasoning that someone who had just tapped through to
 * the store should not be sold to on their way back. That reasoning was wrong in the one way
 * that matters: tapping a store link is not installing anything. The reader comes back to the
 * same browser, on the same surface, still without the deep dive, without search and without
 * Ask, and the one line that explains where those went had removed itself on the strength of a
 * tap we cannot see the result of. The strip is not a consolation prize for saying no; it is
 * the label on this surface, the standing explanation of what this version of InSnip is.
 *
 * A list rather than a comparison so the intent survives a refactor: dropping a state from it
 * is a visible edit that `phone.test.ts` fails on, where `!== "taken"` would read as tidying.
 */
export const NUDGE_AFTER: AppChoice[] = ["unasked", "declined", "taken"];

/** The quiet standing invitation inside the card. Shown to every phone-web reader, whatever
 *  they have already answered about the app, which is what "show them what they are missing"
 *  means once you accept that they are still missing it. */
export function shouldNudgeForApp(): boolean {
  return isPhoneWeb() && NUDGE_AFTER.includes(appChoice());
}

/**
 * Is the screen free for the end-of-round app prompt?
 *
 * A finished round already puts something up: the round summary on the normal path, or the
 * caught-up screen when the bucket is empty. The Desk may be open, and on a first visit the
 * door may still be up. The prompt WAITS for whichever of those is showing rather than
 * stacking on it, because two sheets over one screen is how a celebration turns into an
 * interruption, and this one arrives on every round.
 *
 * `signInOpen` is the one that was NOT obvious, and it was found by driving a real phone to
 * the end of a real round on production rather than by reading the code. A signed-out reader
 * meets the sign-in gate at around the tenth story (showsFirstDoor in App.tsx), which is the
 * same moment a round completes. That gate is a genuine wall: its scrim has no close handler
 * when authWall is set. A marketing sheet racing it would either be buried under it or steal
 * a tap meant for it. So the prompt yields, and a signed-out phone reader sees it after they
 * sign in, not instead of signing in.
 *
 * Pulled out of the effect that uses it so the sequencing is a thing that can be asserted.
 */
export function roundPromptReady(s: {
  due: boolean;
  summaryOpen: boolean;
  deskOpen: boolean;
  doorOpen: boolean;
  caughtUp: boolean;
  signInOpen: boolean;
}): boolean {
  if (!s.due) return false;
  return !s.summaryOpen && !s.deskOpen && !s.doorOpen && !s.caughtUp && !s.signInOpen;
}

// ── What the app actually gives you that a browser tab cannot ───────────────
//
// Every line here has to be TRUE, because it is an argument made to a stranger who can check
// it in one install. No "blazing fast", no invented numbers. Each maps to something that
// genuinely only exists, or only works properly, in the native build.

export interface AppEdge {
  /** A short label for the compact strip. */
  short: string;
  /** The full line for the door. */
  line: string;
  /** A glyph name resolved by the component; kept out of this module so it stays testable. */
  icon: "dive" | "search" | "ask" | "bell" | "flame";
}

/**
 * THESE ARE NOW THE TOOLS THE PHONE READER NO LONGER HAS, not general app virtues.
 *
 * The phone browser was cut back to one job (read the story) in 2026-09-08, which moved four
 * real tools off that surface: the deep dive, search, Ask, and the saved library. That makes
 * this list stronger than the one it replaces, because every line is now a thing the reader
 * has actually just looked for and not found, rather than a claim about smoothness they have
 * no way to check. Keep it that way: if a tool ever comes back to the phone browser, it comes
 * out of this list on the same commit, or the pitch starts lying.
 */
export const APP_EDGES: AppEdge[] = [
  {
    short: "Deep dive",
    line: "Follow a story beat by beat with the deep dive, and see how it actually unfolded.",
    icon: "dive",
  },
  {
    short: "Search everything",
    line: "Search every snippet ever published, by topic, person or idea.",
    icon: "search",
  },
  {
    short: "Ask the story",
    line: "Ask a question about any story and get an answer built from its own sources.",
    icon: "ask",
  },
  {
    short: "Your library, kept",
    line: "Your streak and everything you saved, kept together on your phone.",
    icon: "flame",
  },
];

/** The three tools the compact strip names outright. Short enough for one line on a phone. */
export const APP_MISSING_TOOLS = ["Deep dive", "Search", "Ask"];

/** The store the primary button should point at, by platform. */
export function storeName(platform: WebPlatform = webPlatform()): string {
  if (platform === "ios") return "the App Store";
  if (platform === "android") return "Google Play";
  return "your store";
}
